import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TableOfContents from './components/TableOfContents';
import TermsSection from './components/TermsSection';
import SearchTerms from './components/SearchTerms';
import PrintVersion from './components/PrintVersion';
import ContactLegal from './components/ContactLegal';
import BreadcrumbNav from './components/BreadcrumbNav';

const TermsConditions = () => {
  const [activeSection, setActiveSection] = useState('');
  const [searchHighlight, setSearchHighlight] = useState(null);

  const termsData = [
    {
      id: 'acceptance',
      title: 'Acceptance of Terms',
      icon: 'CheckCircle',
      content: [
        {
          type: 'paragraph',
          text: `By accessing and using the Mava Cold Beverages website (www.mava.in), mobile application, or any of our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions. These terms constitute a legally binding agreement between you and Mava Beverages Pvt. Ltd.`
        },
        {
          type: 'highlight',
          title: 'Important Notice',
          text: `If you do not agree with any part of these terms, you must discontinue use of our services immediately. Continued use of our platform constitutes acceptance of any updates to these terms.`
        },
        {
          type: 'list',
          title: 'By using our services, you confirm that you:',
          items: [
            'Are at least 18 years of age or have parental/guardian consent',
            'Have the legal capacity to enter into binding agreements',
            'Will comply with all applicable laws and regulations',
            'Provide accurate and truthful information when required'
          ]
        }
      ]
    },
    {
      id: 'definitions',
      title: 'Definitions & Interpretations',
      icon: 'BookOpen',
      content: [
        {
          type: 'paragraph',
          text: `The following terms shall have the meanings assigned to them throughout this agreement:`
        },
        {
          type: 'table',
          headers: ['Term', 'Definition'],
          rows: [
            ['Company/We/Us/Our', 'Mava Beverages Pvt. Ltd., a company incorporated under Indian law'],
            ['User/You/Your', 'Any individual or entity accessing or using our services'],
            ['Services', 'All products, website features, mobile app, and related services offered by Mava'],
            ['Products', 'Cold beverage bottles, accessories, and related merchandise sold by Mava'],
            ['Platform', 'Our website, mobile application, and any associated digital interfaces'],
            ['Content', 'All text, images, videos, logos, and other materials on our platform'],
            ['Account', 'User profile created for accessing personalized features and services']
          ]
        }
      ]
    },
    {
      id: 'services',
      title: 'Description of Services',
      icon: 'Package',
      content: [
        {
          type: 'paragraph',
          text: `Mava Cold Beverages provides an e-commerce platform specializing in premium, eco-friendly cold beverage bottles and related accessories. Our services include product browsing, customization options, order placement, customer support, and educational content about hydration and sustainability.`
        },
        {
          type: 'list',
          title: 'Our platform offers:',
          items: [
            'Premium steel, smart, and insulated bottle collections',
            'Product customization and personalization services',
            'Bulk ordering solutions for corporate clients',
            'Educational content about hydration and sustainability',
            'Customer support and warranty services',
            'Community features and user engagement tools'
          ]
        },
        {
          type: 'highlight',
          title: 'Service Availability',
          text: `Services are currently available within India. We reserve the right to modify, suspend, or discontinue any service without prior notice. Product availability may vary based on location and inventory.`
        }
      ]
    },
    {
      id: 'user-accounts',
      title: 'User Accounts & Registration',
      icon: 'User',
      content: [
        {
          type: 'paragraph',
          text: `While browsing our platform does not require registration, certain features such as order tracking, personalized recommendations, and customer support may require creating a user account.`
        },
        {
          type: 'list',
          title: 'Account Registration Requirements:',
          items: [
            'Provide accurate, current, and complete information',
            'Maintain and update your information as necessary',
            'Keep your login credentials secure and confidential',
            'Notify us immediately of any unauthorized account access',
            'Accept responsibility for all activities under your account'
          ]
        },
        {
          type: 'highlight',
          title: 'Account Security',
          text: `You are solely responsible for maintaining the confidentiality of your account credentials. Mava will not be liable for any loss or damage arising from unauthorized account access due to your failure to maintain security.`
        },
        {
          type: 'paragraph',
          text: `We reserve the right to suspend or terminate accounts that violate these terms, engage in fraudulent activities, or pose security risks to our platform or other users.`
        }
      ]
    },
    {
      id: 'orders-payments',
      title: 'Orders & Payment Terms',
      icon: 'CreditCard',
      content: [
        {
          type: 'paragraph',
          text: `All orders placed through our platform are subject to acceptance and availability. We reserve the right to refuse or cancel orders at our discretion, including cases of pricing errors, product unavailability, or suspected fraudulent activity.`
        },
        {
          type: 'list',
          title: 'Order Process:',
          items: [
            'Product selection and customization (if applicable)',
            'Order review and confirmation',
            'Payment processing through secure gateways',
            'Order confirmation and tracking information',
            'Manufacturing and quality assurance (for custom orders)',
            'Shipping and delivery coordination'
          ]
        },
        {
          type: 'table',
          headers: ['Payment Method', 'Processing Time', 'Additional Fees'],
          rows: [
            ['Credit/Debit Cards', 'Instant', 'None'],
            ['Net Banking', 'Instant', 'None'],
            ['UPI/Digital Wallets', 'Instant', 'None'],
            ['Bank Transfer', '1-3 business days', 'None'],
            ['Cash on Delivery', 'Upon delivery', '₹50 handling fee']
          ]
        },
        {
          type: 'highlight',
          title: 'Pricing Policy',
          text: `All prices are listed in Indian Rupees (₹) and include applicable taxes unless otherwise specified. Prices may change without notice, but confirmed orders will honor the price at the time of purchase.`
        }
      ]
    },
    {
      id: 'shipping-delivery',
      title: 'Shipping & Delivery',
      icon: 'Truck',
      content: [
        {
          type: 'paragraph',
          text: `We strive to deliver your Mava products safely and efficiently. Delivery times and methods may vary based on location, product type, and customization requirements.`
        },
        {
          type: 'table',
          headers: ['Delivery Zone', 'Standard Delivery', 'Express Delivery', 'Shipping Cost'],
          rows: [
            ['Metro Cities', '3-5 business days', '1-2 business days', 'Free above ₹1,500'],
            ['Tier 2 Cities', '5-7 business days', '2-3 business days', 'Free above ₹2,000'],
            ['Other Areas', '7-10 business days', '3-5 business days', 'Free above ₹2,500'],
            ['Remote Areas', '10-15 business days', 'Not available', '₹200 flat rate']
          ]
        },
        {
          type: 'list',
          title: 'Delivery Guidelines:',
          items: [
            'Accurate delivery address must be provided during checkout',
            'Someone must be available to receive the package during delivery hours',
            'Photo ID may be required for high-value orders',
            'Delivery attempts will be made during business hours (9 AM - 6 PM)',
            'Failed delivery attempts may result in return to warehouse'
          ]
        },
        {
          type: 'highlight',
          title: 'Custom Orders',
          text: `Personalized and custom bottles require additional manufacturing time of 7-14 business days before shipping. Rush orders may be available for additional fees.`
        }
      ]
    },
    {
      id: 'returns-refunds',
      title: 'Returns & Refund Policy',
      icon: 'RotateCcw',
      content: [
        {
          type: 'paragraph',
          text: `We want you to be completely satisfied with your Mava purchase. Our return and refund policy is designed to ensure customer satisfaction while maintaining fair business practices.`
        },
        {
          type: 'table',
          headers: ['Product Type', 'Return Window', 'Condition Required', 'Refund Method'],
          rows: [
            ['Standard Bottles', '30 days', 'Unused, original packaging', 'Original payment method'],
            ['Custom/Personalized', 'Not returnable', 'Manufacturing defects only', 'Store credit/replacement'],
            ['Accessories', '15 days', 'Unused, original packaging', 'Original payment method'],
            ['Bulk Orders (10+)', '7 days', 'Unopened packages only', 'Bank transfer'],
            ['Gift Items', '45 days', 'Unused, with gift receipt', 'Store credit or exchange']
          ]
        },
        {
          type: 'list',
          title: 'Return Process:',
          items: [
            'Contact customer support to initiate return request',
            'Receive return authorization and shipping instructions',
            'Package items securely in original packaging',
            'Ship using provided return label or approved courier',
            'Receive confirmation once return is processed'
          ]
        },
        {
          type: 'highlight',
          title: 'Refund Timeline',
          text: `Refunds are processed within 5-7 business days after we receive and inspect returned items. The time for funds to appear in your account depends on your payment method and bank processing times.`
        }
      ]
    },
    {
      id: 'intellectual-property',
      title: 'Intellectual Property Rights',
      icon: 'Copyright',
      content: [
        {
          type: 'paragraph',
          text: `All content on the Mava platform, including but not limited to text, graphics, logos, images, videos, software, and design elements, is the exclusive property of Mava Beverages Pvt. Ltd. or its licensors and is protected by Indian and international copyright, trademark, and other intellectual property laws.`
        },
        {
          type: 'list',
          title: 'Protected Materials Include:',
          items: [
            'Mava brand name, logos, and trademarks',
            'Product designs, images, and descriptions',
            'Website design, layout, and user interface',
            'Marketing materials and promotional content',
            'Software, applications, and technical innovations',
            'Educational content and sustainability resources'
          ]
        },
        {
          type: 'highlight',
          title: 'Usage Restrictions',
          text: `You may not reproduce, distribute, modify, create derivative works, publicly display, or commercially exploit any of our intellectual property without explicit written permission from Mava Beverages Pvt. Ltd.`
        },
        {
          type: 'paragraph',
          text: `Limited license is granted for personal, non-commercial use of our platform. This license does not include rights to download, copy, or redistribute content for any purpose other than viewing on our platform.`
        }
      ]
    },
    {
      id: 'user-conduct',
      title: 'User Conduct & Prohibited Activities',
      icon: 'Shield',
      content: [
        {
          type: 'paragraph',
          text: `Users must conduct themselves responsibly and lawfully when using our platform. The following activities are strictly prohibited and may result in account suspension or legal action.`
        },
        {
          type: 'list',
          title: 'Prohibited Activities:',
          items: [
            'Providing false, misleading, or fraudulent information',
            'Attempting to gain unauthorized access to our systems',
            'Uploading malicious software, viruses, or harmful code',
            'Harassing, threatening, or abusing other users or staff',
            'Violating any applicable laws or regulations',
            'Infringing on intellectual property rights',
            'Engaging in spam, phishing, or other deceptive practices',
            'Attempting to manipulate prices, reviews, or ratings'
          ]
        },
        {
          type: 'highlight',
          title: 'Community Standards',
          text: `We foster a positive community environment. Users are expected to be respectful, honest, and constructive in all interactions. Hate speech, discrimination, and inappropriate content will not be tolerated.`
        },
        {
          type: 'paragraph',
          text: `We reserve the right to monitor user activity, investigate violations, and take appropriate action including warnings, account suspension, or permanent bans. Serious violations may be reported to law enforcement authorities.`
        }
      ]
    },
    {
      id: 'privacy-data',
      title: 'Privacy & Data Protection',
      icon: 'Lock',
      content: [
        {
          type: 'paragraph',
          text: `Your privacy is important to us. Our collection, use, and protection of personal information is governed by our Privacy Policy, which forms an integral part of these Terms and Conditions.`
        },
        {
          type: 'list',
          title: 'Data We Collect:',
          items: [
            'Personal information provided during registration and orders',
            'Payment and billing information for transaction processing',
            'Device and browser information for platform optimization',
            'Usage patterns and preferences for personalization',
            'Communication records for customer support',
            'Location data for delivery and service customization'
          ]
        },
        {
          type: 'highlight',
          title: 'Data Security',
          text: `We implement industry-standard security measures to protect your personal information. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.`
        },
        {
          type: 'paragraph',
          text: `You have rights regarding your personal data, including access, correction, deletion, and portability. For detailed information about our privacy practices, please review our Privacy Policy.`
        }
      ]
    },
    {
      id: 'warranties-disclaimers',
      title: 'Warranties & Disclaimers',
      icon: 'AlertTriangle',
      content: [
        {
          type: 'paragraph',
          text: `Mava Cold Beverages stands behind the quality of our products and provides specific warranties as outlined below. However, certain disclaimers apply to limit our liability and clarify user expectations.`
        },
        {
          type: 'table',
          headers: ['Product Category', 'Warranty Period', 'Coverage', 'Exclusions'],
          rows: [
            ['Steel Bottles', '2 years', 'Manufacturing defects, insulation failure', 'Normal wear, misuse, accidents'],
            ['Smart Bottles', '1 year', 'Electronic components, app connectivity', 'Water damage, battery degradation'],
            ['Accessories', '6 months', 'Material defects, functionality issues', 'Cosmetic damage, normal wear'],
            ['Custom Products', '1 year', 'Manufacturing defects only', 'Design changes, personalization errors']
          ]
        },
        {
          type: 'highlight',
          title: 'Service Disclaimer',
          text: `Our platform and services are provided "as is" without warranties of any kind. We do not guarantee uninterrupted service, error-free operation, or that our platform will meet all user requirements.`
        },
        {
          type: 'list',
          title: 'Limitation of Liability:',
          items: [
            'Our liability is limited to the purchase price of products',
            'We are not liable for indirect, consequential, or punitive damages',
            'Force majeure events are excluded from liability',
            'Third-party service failures are not our responsibility',
            'User misuse or negligence voids warranty coverage'
          ]
        }
      ]
    },
    {
      id: 'termination',
      title: 'Termination of Services',
      icon: 'XCircle',
      content: [
        {
          type: 'paragraph',
          text: `Either party may terminate this agreement under certain circumstances. We reserve the right to suspend or terminate user accounts and access to our services for violations of these terms or other legitimate business reasons.`
        },
        {
          type: 'list',
          title: 'Grounds for Termination:',
          items: [
            'Violation of terms and conditions or policies',
            'Fraudulent or illegal activities',
            'Abuse of our platform or services',
            'Non-payment of outstanding amounts',
            'Repeated customer service issues',
            'Business closure or service discontinuation'
          ]
        },
        {
          type: 'highlight',
          title: 'Effect of Termination',
          text: `Upon termination, your right to use our services ceases immediately. Outstanding orders will be fulfilled according to existing terms, but new orders will not be accepted.`
        },
        {
          type: 'paragraph',
          text: `Users may terminate their accounts at any time by contacting customer support. Account closure does not affect existing warranties, pending orders, or legal obligations that survive termination.`
        }
      ]
    },
    {
      id: 'governing-law',
      title: 'Governing Law & Dispute Resolution',
      icon: 'Scale',
      content: [
        {
          type: 'paragraph',
          text: `These Terms and Conditions are governed by the laws of India. Any disputes arising from or relating to these terms or our services shall be subject to the exclusive jurisdiction of courts in Gurgaon, Haryana, India.`
        },
        {
          type: 'list',
          title: 'Dispute Resolution Process:',
          items: [
            'Direct negotiation between parties for 30 days',
            'Mediation through mutually agreed mediator',
            'Arbitration under Indian Arbitration and Conciliation Act',
            'Court proceedings as last resort in Gurgaon jurisdiction',
            'All proceedings to be conducted in English language'
          ]
        },
        {
          type: 'highlight',
          title: 'Consumer Rights',
          text: `Nothing in these terms affects your statutory rights as a consumer under Indian consumer protection laws. These terms are in addition to, not in place of, your legal rights.`
        },
        {
          type: 'paragraph',
          text: `For international users, local consumer protection laws may apply in addition to these terms. However, Indian law shall govern the interpretation and enforcement of this agreement.`
        }
      ]
    },
    {
      id: 'modifications',
      title: 'Modifications & Updates',
      icon: 'Edit',
      content: [
        {
          type: 'paragraph',
          text: `Mava Beverages Pvt. Ltd. reserves the right to modify, update, or revise these Terms and Conditions at any time to reflect changes in our services, legal requirements, or business practices.`
        },
        {
          type: 'list',
          title: 'Notification Process:',
          items: [
            'Email notification to registered users',
            'Prominent notice on our website homepage',
            'In-app notifications for mobile users',
            'Social media announcements for major changes',
            '30-day advance notice for significant modifications'
          ]
        },
        {
          type: 'highlight',
          title: 'Acceptance of Changes',
          text: `Continued use of our services after notification constitutes acceptance of modified terms. If you disagree with changes, you must discontinue use of our services.`
        },
        {
          type: 'paragraph',
          text: `We recommend reviewing these terms periodically to stay informed of any updates. The "Last Updated" date at the top of this document indicates when the most recent changes were made.`
        }
      ]
    }
  ];

  // Track active section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const sections = termsData?.map(section => section?.id);
      const scrollPosition = window.scrollY + 150;

      for (let i = sections?.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections?.[i]);
        if (element && element?.offsetTop <= scrollPosition) {
          setActiveSection(sections?.[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Set initial active section

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearchResult = (sectionId) => {
    setSearchHighlight(sectionId);
    if (sectionId) {
      setTimeout(() => setSearchHighlight(null), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Terms & Conditions - Mava Cold Beverages | Legal Information</title>
        <meta name="description" content="Read Mava Cold Beverages' terms and conditions. Understand our policies on orders, returns, warranties, and user responsibilities for our premium eco-friendly bottles." />
        <meta name="keywords" content="terms conditions, legal policy, mava beverages, user agreement, warranty policy, return policy" />
        <meta property="og:title" content="Terms & Conditions - Mava Cold Beverages" />
        <meta property="og:description" content="Complete terms and conditions for Mava Cold Beverages services and products." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://mava.in/terms-conditions" />
      </Helmet>
      <Header />
      <main className="pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/10 via-accent/5 to-background py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <BreadcrumbNav />
            
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="FileText" size={40} className="text-primary" />
              </div>
              <h1 className="font-poppins font-bold text-3xl lg:text-4xl text-text-primary mb-4">
                Terms & Conditions
              </h1>
              <p className="text-lg text-text-secondary font-inter max-w-2xl mx-auto mb-6">
                Please read these terms carefully before using our services. 
                Your use of Mava's platform constitutes acceptance of these conditions.
              </p>
              <div className="flex items-center justify-center space-x-4 text-sm text-text-secondary">
                <div className="flex items-center space-x-2">
                  <Icon name="Calendar" size={16} />
                  <span>Last Updated: 03/11/2025</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Clock" size={16} />
                  <span>Effective Date: 01/11/2025</span>
                </div>
              </div>
            </div>

            {/* Search Terms */}
            <div className="max-w-md mx-auto">
              <SearchTerms sections={termsData} onSearchResult={handleSearchResult} />
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-4 gap-8">
              {/* Sidebar */}
              <div className="lg:col-span-1">
                <TableOfContents sections={termsData} activeSection={activeSection} />
              </div>

              {/* Content */}
              <div className="lg:col-span-3">
                <PrintVersion sections={termsData} />
                
                <div className="space-y-12">
                  {termsData?.map((section) => (
                    <TermsSection
                      key={section?.id}
                      section={section}
                      isHighlighted={searchHighlight === section?.id}
                    />
                  ))}
                </div>

                <ContactLegal />

                {/* Footer Navigation */}
                <div className="bg-muted/50 border border-border rounded-lg p-6 mt-12">
                  <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                    <div className="text-center sm:text-left">
                      <h3 className="font-poppins font-semibold text-text-primary mb-1">
                        Need More Information?
                      </h3>
                      <p className="text-sm text-text-secondary">
                        Explore our other legal documents and policies
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.location.href = '/privacy-policy'}
                        iconName="Shield"
                        iconPosition="left"
                      >
                        Privacy Policy
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => window.location.href = '/contact'}
                        iconName="MessageCircle"
                        iconPosition="left"
                        className="bg-action hover:bg-action/90"
                      >
                        Contact Support
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      {/* Scroll to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-6 right-6 w-12 h-12 bg-primary text-primary-foreground rounded-full liquid-shadow flex items-center justify-center hover:bg-primary/90 brand-transition z-40"
        aria-label="Scroll to top"
      >
        <Icon name="ArrowUp" size={20} />
      </button>
    </div>
  );
};

export default TermsConditions;